import { useState, useEffect } from 'react';
import axios from 'axios';
import { PackageIcon } from 'lucide-react';
import { DataTable, EmptyState, Panel, Pill, SkeletonTable, type PillTone } from '@bcecloud/portal-ui';
import { getToken } from '@/lib/auth';
import { useEventContext } from './useEventContext';

/** One shipment row, as the shipments endpoint returns it. */
interface Shipment {
  _id: string;
  vendor?: string;
  carrier?: string;
  trackingNumber?: string;
  status?: string;
  eta?: string;
  arrivalDate?: string;
  link?: string;
  items?: { name?: string; quantity?: string }[];
}

/** Pill tones per shipment status. */
const STATUS_TONES: Readonly<Record<string, PillTone>> = {
  DELIVERED: 'success',
  IN_TRANSIT: 'brand',
  PRE_TRANSIT: 'warning',
};

/**
 * Formats a date for display.
 *
 * @param dateStr - ISO date.
 * @returns e.g. "Aug 8, 2026".
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Event shipping tab — shipment tracking as the standard table.
 */
export function EventShippingTab(): React.ReactElement {
  const { event } = useEventContext();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchShipments(): Promise<void> {
      if (!event?._id) return;
      try {
        const token = getToken();
        // The V3 list scopes by role server-side: clients only ever receive
        // client-viewable shipments for events they are a contact on.
        const { data } = await axios.get(`/api/v3/events/${event._id}/shipments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setShipments(data.data || []);
      } catch {
        if (!cancelled) setShipments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchShipments();
    return () => {
      cancelled = true;
    };
  }, [event?._id]);

  if (loading) {
    return <SkeletonTable rows={3} columns={['w-[20%]', 'w-[25%]']} label="Loading shipments…" />;
  }

  if (shipments.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<PackageIcon aria-hidden className="size-6" />}
          title="No shipments"
          message="No shipments have been created for this event yet — tracking appears here as soon as materials ship."
        />
      </Panel>
    );
  }

  return (
    <DataTable headers={['Vendor', 'Carrier', 'Tracking #', 'Status', 'ETA', 'Items']} minWidth={720}>
      {shipments.map((shipment) => (
        <tr
          key={shipment._id}
          className="border-t border-portal-line align-middle text-portal-tiny"
        >
          <td className="px-4 py-3">{shipment.vendor ?? '—'}</td>
          <td className="px-4 py-3 text-portal-muted">
            {shipment.carrier
              ? shipment.carrier.charAt(0).toUpperCase() + shipment.carrier.slice(1)
              : '—'}
          </td>
          <td className="px-4 py-3">
            {shipment.trackingNumber ? (
              shipment.link ? (
                <a
                  href={shipment.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-portal-micro text-brand-link hover:underline"
                >
                  {shipment.trackingNumber}
                </a>
              ) : (
                <span className="font-mono text-portal-micro">{shipment.trackingNumber}</span>
              )
            ) : (
              '—'
            )}
          </td>
          <td className="px-4 py-3">
            {shipment.status ? (
              <Pill tone={STATUS_TONES[shipment.status] ?? 'neutral'}>
                {shipment.status.replace(/_/g, ' ')}
              </Pill>
            ) : (
              '—'
            )}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-portal-muted">
            {formatDate(shipment.eta)}
          </td>
          <td className="px-4 py-3 text-portal-muted">
            {shipment.items && shipment.items.length > 0
              ? `${shipment.items.length} item${shipment.items.length === 1 ? '' : 's'}`
              : '—'}
          </td>
        </tr>
      ))}
    </DataTable>
  );
}
